import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    fetchCommissionOpeners, createCommissionOpener, updateCommissionOpener, deleteCommissionOpener,
    fetchCommissionUsers,
} from '../../api';
import DataGrid from '../common/DataGrid';
import Modal from '../marketing/Modal';
import { actionsColumn, formatDate, toISODate } from '../marketing/shared';
import { formatDay, openerStatus } from './shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Commissions.css';

// Agents: everyone who earns commission, as a split agent on a case or as the
// settlement officer on a payment. Most are just a name, since many never
// sign in to this app; one can be linked to an app user when they have a
// login. Importing a payment report adds each settlement officer by the name
// Logics gives them.
//
// When someone quits or is let go, mark them inactive with their last day.
// Payments after that day earn them nothing, so they drop off the Earned
// report from then on; what they earned before stays. Someone with commission
// history can't be deleted, only marked inactive.

const EMPTY_FORM = { name: '', userId: '', ended: false, endDate: '' };

export default function Openers() {
    const [openers, setOpeners] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // The open dialog: { mode: 'create' | 'edit' | 'delete', opener }.
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [o, u] = await Promise.all([fetchCommissionOpeners(), fetchCommissionUsers()]);
            setOpeners(o || []);
            setUsers(u || []);
        } catch (err) {
            setError(err.message);
            setOpeners([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setDialogError(null);
        setDialog({ mode: 'create' });
    };

    const openEdit = useCallback((opener) => {
        setForm({
            name: opener.name,
            userId: opener.user_id ? String(opener.user_id) : '',
            ended: Boolean(opener.end_date),
            endDate: opener.end_date || '',
        });
        setDialogError(null);
        setDialog({ mode: 'edit', opener });
    }, []);

    const openDelete = useCallback((opener) => {
        setDialogError(null);
        setDialog({ mode: 'delete', opener });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    // Picking a user fills in the name when it's still empty.
    const pickUser = (e) => {
        const userId = e.target.value;
        const user = users.find((u) => String(u.id) === userId);
        setForm((f) => ({ ...f, userId, name: (f.name.trim() || !user) ? f.name : user.name }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            const body = {
                name: form.name.trim(),
                user_id: form.userId ? Number(form.userId) : null,
                end_date: form.ended ? form.endDate : null,
            };
            if (dialog.mode === 'create') await createCommissionOpener(body);
            else if (dialog.mode === 'edit') await updateCommissionOpener(dialog.opener.id, body);
            else await deleteCommissionOpener(dialog.opener.id);
            setDialog(null);
            await load();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Users who aren't another opener's login, plus this opener's own.
    const linkableUsers = useMemo(() => {
        const self = dialog?.opener?.id;
        return users.filter((u) => !u.opener_id || u.opener_id === self);
    }, [users, dialog]);

    const columnDefs = useMemo(() => [
        { field: 'name', headerName: 'Agent', flex: 1, minWidth: 180 },
        {
            field: 'user_email',
            headerName: 'App Login',
            minWidth: 200,
            valueFormatter: (p) => p.value || '—',
        },
        {
            colId: 'status',
            headerName: 'Status',
            minWidth: 170,
            valueGetter: (p) => (p.data ? openerStatus(p.data.active, p.data.end_date) : ''),
        },
        { field: 'splits', headerName: 'Splits', type: 'rightAligned', width: 100, filter: 'agNumberColumnFilter' },
        { field: 'officer_cases', headerName: 'Officer Cases', headerTooltip: 'Cases with a payment naming them as settlement officer', type: 'rightAligned', width: 130, filter: 'agNumberColumnFilter' },
        { field: 'created_at', headerName: 'Added', width: 120, valueFormatter: (p) => formatDate(p.value) },
        actionsColumn(openEdit, openDelete),
    ], [openEdit, openDelete]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 90 }), []);

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Agents</h1>
                    <p className="page__subtitle">
                        Everyone who earns commission: split agents and settlement officers. Settlement officers are
                        added when a payment report names them. An agent doesn't need a login to this app; link one to
                        an app user only if they have one.
                    </p>
                </div>
                <div className="mkt__header-actions">
                    <button className="blueButton" onClick={openCreate}>+ Add Agent</button>
                </div>
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}
            {loading && <p className="page__subtitle">Loading agents…</p>}

            {!loading && !error && openers.length === 0 && (
                <p className="page__subtitle" style={{ marginTop: 16 }}>
                    No agents yet. Add one here, type a new name when adding a split, or import a payment report.
                </p>
            )}

            {openers.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <DataGrid
                        rowData={openers}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        height="calc(100vh - 260px)"
                        enableExport
                        exportFileName={() => `Commission_Agents_${toISODate(new Date())}`}
                    />
                </div>
            )}

            {dialog && dialog.mode !== 'delete' && (
                <Modal
                    title={dialog.mode === 'create' ? 'Add Agent' : 'Edit Agent'}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel={dialog.mode === 'create' ? 'Add Agent' : 'Save Changes'}
                    submitting={saving}
                    submitDisabled={!form.name.trim() || (form.ended && !form.endDate)}
                    error={dialogError}
                >
                    <label className="mkt__field">
                        <span>Name</span>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="e.g. Sam Smith"
                            autoFocus
                        />
                        <span className="com__hint">
                            Use the name as it appears in Logics: settlement officers are matched to the payment report
                            by name.
                        </span>
                    </label>
                    <label className="mkt__field">
                        <span>App Login (optional)</span>
                        <select value={form.userId} onChange={pickUser}>
                            <option value="">None: not an app user</option>
                            {linkableUsers.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.name}{u.name !== u.email ? ` (${u.email})` : ''}{u.active ? '' : ' (inactive)'}
                                </option>
                            ))}
                        </select>
                    </label>
                    {dialog.mode === 'edit' && (<>
                        <label className="mkt__check">
                            <input
                                type="checkbox"
                                checked={form.ended}
                                onChange={(e) => {
                                    const ended = e.target.checked;
                                    // Default the last day to today when marking someone inactive.
                                    setForm((f) => ({ ...f, ended, endDate: ended ? (f.endDate || toISODate(new Date())) : '' }));
                                }}
                            />
                            Inactive: quit or let go
                        </label>
                        {form.ended && (
                            <label className="mkt__field">
                                <span>Last day earning commission</span>
                                <input
                                    type="date"
                                    value={form.endDate}
                                    onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                                />
                                <span className="com__hint">
                                    Payments dated after {form.endDate ? formatDay(form.endDate) : 'this day'} earn them
                                    nothing, so they stop showing on the Earned report from then on. Commission they
                                    earned up to this day stays.
                                </span>
                            </label>
                        )}
                    </>)}
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Agent"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Delete Agent"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete <strong>{dialog.opener.name}</strong>? This can't be undone.
                    </p>
                    {(dialog.opener.splits > 0 || dialog.opener.officer_cases > 0) && (
                        <p className="mkt-modal__text">
                            They have commission history, so they can't be deleted. Edit them and mark them inactive with
                            their last day instead.
                        </p>
                    )}
                </Modal>
            )}
        </div>
    );
}
